import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, HandCoins, Scale, Check,
  AlertTriangle, Loader2, CheckCircle2, BadgeEuro, ShieldCheck,
  MapPin, UserRound, Mail, Phone, ArrowRight, ArrowLeft, Coins, Calculator, Users,
  FileText, Flag, Award,
} from "lucide-react";
import Seo from "@/components/v2/Seo";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import TraceCard from "@/components/v2/TraceCard";
import { Section } from "@/components/v2/blocks";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import api, { formatApiErrorDetail } from "@/lib/api";

/* ────────────────────────────────────────────────────────────────────────────
   /primes-cee — Certificats d'Économies d'Énergie, 6ᵉ période (2026-2030).
   Positionnement : montage et suivi du dossier CEE sans reste à charge,
   PAS délégataire. Contenu à jour de juillet 2026 (réforme Coup de pouce,
   80ᵉ arrêté, agrément PAC…) — voir les notes réglementaires en commentaire.
   Le formulaire d'estimation poste sur /contact-messages (même canal CRM que
   LeadFormV2) avec un message composé — aucun changement backend requis.
   ──────────────────────────────────────────────────────────────────────────── */

const TRACE_COLORS = ["#2E7D62", "#7AB317", "#0E9C8A"];

/* ---- FAQ (réutilisée pour le JSON-LD) ---- */
const FAQ = [
  [
    "Qui paie réellement la prime CEE ?",
    "Les vendeurs d'énergie (électricité, gaz, carburants, fioul), dits « obligés ». L'État leur impose un quota d'économies d'énergie à financer, sous peine de pénalités. Ils achètent donc des certificats en finançant vos travaux — directement ou via des délégataires. C'est aussi pour cela que notre service est gratuit pour vous.",
  ],
  [
    "La prime CEE est-elle cumulable avec MaPrimeRénov' ?",
    "Les primes CEE classiques se cumulent avec MaPrimeRénov', la TVA à 5,5 % et l'éco-PTZ. En revanche, le Coup de pouce Rénovation d'ampleur ne se cumule pas avec le parcours accompagné de MaPrimeRénov' : c'est une voie alternative, réservée aux ménages modestes et très modestes. Nous vérifions le meilleur montage pour votre situation.",
  ],
  [
    "Pourquoi les montants ne sont-ils plus fixes en 2026 ?",
    "Depuis la réforme du Coup de pouce chauffage (opérations engagées à partir du 1ᵉʳ janvier 2026), les montants forfaitaires des anciennes chartes ne s'appliquent plus : la prime est calculée au cas par cas selon la performance de l'équipement (Etas), la zone climatique (H1, H2, H3) et la surface chauffée. Deux devis identiques peuvent donner des primes différentes selon l'offre de valorisation retenue — d'où l'intérêt de comparer.",
  ],
  [
    "Quels délais pour toucher la prime ?",
    "La prime est soit déduite directement du devis par l'installateur partenaire, soit versée après la fin des travaux et la validation du dossier — comptez généralement quelques semaines à quelques mois selon l'opération. Les conditions de versement font partie des critères que nous comparons.",
  ],
  [
    "Energy Market est-il un délégataire CEE ?",
    "Non, et c'est volontaire : nous restons indépendants. Nous nous concentrons sur les opérations finançables sans reste à charge et montons le dossier jusqu'au versement, exactement avec le même soin que pour vos contrats d'électricité et de gaz.",
  ],
];

/* ---- Formulaire d'estimation — poste sur le CRM live (/contact-messages) ---- */
const inputCls =
  "w-full rounded-xl border border-white/50 bg-white/30 px-4 py-2.5 text-sm backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

const PROFILS = [
  "Particulier — propriétaire occupant",
  "Particulier — bailleur ou locataire",
  "Copropriété / syndic",
  "Entreprise / tertiaire",
  "Collectivité / bailleur social",
];
const PROJETS = [
  "Remplacement du chauffage (PAC, biomasse…)",
  "Rénovation d'ampleur (2 classes DPE ou +)",
  "Isolation (murs, toiture, planchers)",
  "Raccordement à un réseau de chaleur",
  "Autre projet / plusieurs travaux",
];

const WIZARD_TOTAL = 3;

function WizardField({ icon: Ic, label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-semibold text-primary">{label}</label>
      <div className="relative mt-1.5">
        <Ic className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" aria-hidden />
        {children}
      </div>
    </div>
  );
}

function EstimWizard() {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState({
    profil: PROFILS[0],
    projet: PROJETS[0],
    postal_code: "",
    message: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (step < WIZARD_TOTAL) {
      setStep(step + 1);
      return;
    }
    if (!consent) {
      toast.error("Merci d'accepter la politique de confidentialité.");
      return;
    }
    setLoading(true);
    const message = [
      "Type de demande : Estimation prime CEE (source: primes-cee)",
      `Profil : ${values.profil}`,
      `Projet : ${values.projet}`,
      values.postal_code ? `Code postal : ${values.postal_code}` : null,
      values.phone ? `Téléphone : ${values.phone}` : null,
      "",
      values.message.trim() || "(Pas de message saisi — demande d'estimation de prime CEE.)",
    ]
      .filter((x) => x !== null)
      .join("\n");
    try {
      await api.post("/contact-messages", {
        full_name: values.full_name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        message,
      });
      setDone(true);
      toast.success("Votre demande a bien été reçue.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Envoi impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-3xl border border-white/50 bg-card/25 p-8 text-center shadow-2xl backdrop-blur-2xl">
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
        <h3 className="font-display text-2xl font-bold text-primary mt-4">Votre demande a bien été reçue</h3>
        <p className="text-muted-foreground mt-2">
          Un conseiller vérifie votre éligibilité et revient vers vous sous 24 h ouvrées avec une estimation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-3xl border border-white/50 bg-card/25 p-6 md:p-8 shadow-2xl backdrop-blur-2xl">
      {/* Progression */}
      <p className="text-sm font-bold text-primary">Étape {step} sur {WIZARD_TOTAL}</p>
      <div className="mt-2.5 h-1.5 rounded-full bg-secondary" role="progressbar" aria-valuemin={1} aria-valuemax={WIZARD_TOTAL} aria-valuenow={step} aria-label={`Étape ${step} sur ${WIZARD_TOTAL}`}>
        <div className="h-full rounded-full bg-accent transition-all duration-500" style={{ width: `${(step / WIZARD_TOTAL) * 100}%` }} />
      </div>

      <div className="mt-6 space-y-4">
        {step === 1 && (
          <>
            <WizardField icon={UserRound} label="Vous êtes" htmlFor="cee-profil">
              <select id="cee-profil" value={values.profil} onChange={set("profil")} className={`${inputCls} pl-10`}>
                {PROFILS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </WizardField>
            <WizardField icon={Home} label="Votre projet" htmlFor="cee-projet">
              <select id="cee-projet" value={values.projet} onChange={set("projet")} className={`${inputCls} pl-10`}>
                {PROJETS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </WizardField>
            <Button type="submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              Commencer mon estimation <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <WizardField icon={MapPin} label="Code postal du chantier" htmlFor="cee-cp">
              <input id="cee-cp" inputMode="numeric" value={values.postal_code} onChange={set("postal_code")} className={`${inputCls} pl-10`} placeholder="75001" />
            </WizardField>
            <div>
              <label htmlFor="cee-msg" className="text-sm font-semibold text-primary">
                Précisions sur vos travaux <span className="font-normal text-muted-foreground">(facultatif)</span>
              </label>
              <textarea id="cee-msg" rows={4} value={values.message} onChange={set("message")} className={`${inputCls} mt-1.5`} placeholder="Type de logement, chauffage actuel, surface…" />
            </div>
            <Button type="submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              Continuer <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <WizardField icon={UserRound} label="Nom et prénom *" htmlFor="cee-nom">
              <input id="cee-nom" required minLength={2} value={values.full_name} onChange={set("full_name")} className={`${inputCls} pl-10`} placeholder="Jean Dupont" />
            </WizardField>
            <WizardField icon={Mail} label="E-mail *" htmlFor="cee-email">
              <input id="cee-email" type="email" required value={values.email} onChange={set("email")} className={`${inputCls} pl-10`} placeholder="jean.dupont@email.fr" />
            </WizardField>
            <WizardField icon={Phone} label="Téléphone" htmlFor="cee-tel">
              <input id="cee-tel" value={values.phone} onChange={set("phone")} className={`${inputCls} pl-10`} placeholder="06 12 34 56 78" />
            </WizardField>
            <label className="flex items-start gap-3 text-sm text-muted-foreground">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              <span>J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la politique de confidentialité.</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi…
                </>
              ) : (
                <>
                  Recevoir mon estimation <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        {step > 1 ? (
          <button type="button" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Retour
          </button>
        ) : (
          <span />
        )}
        <p className="text-[11px] text-muted-foreground">Gratuit · Sans engagement</p>
      </div>
    </form>
  );
}

/* ---- Ce qui change en 2026 (bandeau sombre) ---- */
const CHANGES_2026 = [
  {
    icon: Coins,
    title: "Plus de moyens, plus de primes",
    text: "L'obligation passe à 1 050 TWh cumac par an (+27,5 % par rapport au niveau d'obligation actuel). Plus d'obligation = plus de demande de certificats = un contexte favorable aux montants de primes.",
  },
  {
    icon: Calculator,
    title: "Fin des forfaits : calcul sur mesure",
    text: "Pour le chauffage, les montants forfaitaires des anciennes chartes ne s'appliquent plus : la prime dépend de la performance réelle de l'équipement (Etas), de la zone climatique et de la surface chauffée. Comparer les offres devient décisif.",
  },
  {
    icon: ShieldCheck,
    title: "Contrôles renforcés",
    text: "Durabilité des équipements exigée (6 ans minimum dès avril 2026) et agrément des modèles de PAC pour la prime bonifiée à partir de septembre 2026 — la première liste officielle est parue en juillet 2026. La qualité du montage de dossier compte plus que jamais.",
  },
  {
    icon: Users,
    title: "Priorité aux ménages modestes",
    text: "Près de 27 % de l'obligation est fléchée vers la lutte contre la précarité énergétique. Les bonifications « Coup de pouce » restent réservées ou majorées pour les revenus modestes.",
  },
];

const TIMELINE_P6 = [
  { year: "2025", label: "Fin de la 5ᵉ période", now: false },
  { year: "2026", label: "Début de la 6ᵉ période (P6)", now: true },
  { year: "2030", label: "Fin de la 6ᵉ période", now: false },
];

/* Illustration filaire « 2026 » — traits fins, dessin progressif à l'apparition */
function Illu2026() {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setOn(true), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* Rejoue le dessin au survol : reset instantané (sans transition), puis redraw */
  const replay = () => {
    setOn(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setOn(true)));
  };

  const draw = (delay) => ({
    strokeDasharray: 1,
    strokeDashoffset: on ? 0 : 1,
    transition: on ? `stroke-dashoffset 1.4s cubic-bezier(0.6,0,0.3,1) ${delay}s` : "none",
  });
  const scale = [
    { l: "A", y: 133, w: 115, h: 30, r: 15, c: "#D8FF3E", solid: true, op: 1 },
    { l: "B", y: 169, w: 102, h: 27, r: 13.5, c: "#BCEB3C", fo: 0.3, op: 0.86 },
    { l: "C", y: 202, w: 91, h: 27, r: 13.5, c: "#AAD449", fo: 0.22, op: 0.72 },
    { l: "D", y: 235, w: 80, h: 27, r: 13.5, c: "#98BD51", fo: 0.18, op: 0.58 },
    { l: "E", y: 268, w: 70, h: 27, r: 13.5, c: "#86A659", fo: 0.14, op: 0.44 },
    { l: "F", y: 301, w: 61, h: 27, r: 13.5, c: "#748F61", fo: 0.12, op: 0.34 },
    { l: "G", y: 334, w: 53, h: 27, r: 13.5, c: "#627869", fo: 0.1, op: 0.26 },
  ];
  return (
    <svg ref={ref} onMouseEnter={replay} viewBox="0 0 1200 420" className="w-full max-w-xl" fill="none" aria-hidden>
      <defs>
        <linearGradient id="cee26-lime" x1="66" y1="72" x2="1091" y2="346" gradientUnits="userSpaceOnUse">
          <stop stopColor="#D8FF3E" />
          <stop offset="0.52" stopColor="#B8F02F" />
          <stop offset="1" stopColor="#7FBC38" />
        </linearGradient>
        <linearGradient id="cee26-fade" x1="593" y1="98" x2="1120" y2="340" gradientUnits="userSpaceOnUse">
          <stop stopColor="#D8FF3E" stopOpacity="0.14" />
          <stop offset="1" stopColor="#D8FF3E" stopOpacity="0.02" />
        </linearGradient>
        <pattern id="cee26-dots" width="26" height="26" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.5" fill="#B8E83A" opacity="0.16" />
        </pattern>
        <filter id="cee26-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id="cee26-yearclip">
          <rect x="52" y="76" width="600" height="246" rx="20" />
        </clipPath>
      </defs>

      {/* trames et accents de fond */}
      <path d="M56 336C287 287 506 299 699 266C866 238 995 207 1142 170" stroke="#A7D63B" strokeOpacity="0.12" strokeWidth="1" />
      <path d="M48 350C286 313 492 324 706 289C876 261 1015 226 1150 194" stroke="#A7D63B" strokeOpacity="0.08" strokeWidth="0.8" />
      <rect x="450" y="42" width="694" height="322" fill="url(#cee26-dots)" />
      <circle cx="902" cy="208" r="176" stroke="#B8F02F" strokeOpacity="0.08" strokeWidth="0.8" />
      <circle cx="902" cy="208" r="141" stroke="#B8F02F" strokeOpacity="0.1" strokeWidth="0.8" />
      <path d="M44 349C350 314 624 335 1150 180" stroke="#B8F02F" strokeOpacity="0.12" strokeWidth="1.2" />

      {/* 2026 en contour fin */}
      <g clipPath="url(#cee26-yearclip)">
        <text
          x="50"
          y="292"
          fontFamily="'Bricolage Grotesque','Outfit',sans-serif"
          fontSize="250"
          fontWeight="800"
          letterSpacing="-18"
          fill="url(#cee26-fade)"
          stroke="url(#cee26-lime)"
          strokeWidth="2"
          style={{ opacity: on ? 1 : 0, transition: on ? "opacity 1.1s ease 0.15s" : "none" }}
        >
          2026
        </text>
        <path d="M25 306H656" stroke="url(#cee26-lime)" strokeWidth="4" strokeLinecap="round" pathLength="1" style={draw(0.1)} />
      </g>
      <circle cx="66" cy="306" r="5" fill="#D8FF3E" filter="url(#cee26-glow)" className="cee26-pulse" />

      {/* maison minimale */}
      <g stroke="url(#cee26-lime)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M692 165L823 61L954 165" pathLength="1" style={draw(0.35)} />
        <path d="M720 143V316H926V143" pathLength="1" style={draw(0.5)} />
        <path d="M804 76V43H836" pathLength="1" style={draw(0.8)} />
      </g>
      <path d="M845 316V250H893V316" stroke="url(#cee26-lime)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" pathLength="1" style={draw(0.9)} />

      {/* feuille */}
      <g stroke="url(#cee26-lime)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M719 270C672 272 647 302 651 342C694 339 720 314 719 270Z" pathLength="1" style={draw(1.05)} />
        <path d="M657 335C676 313 694 296 713 280" pathLength="1" style={draw(1.25)} />
      </g>

      {/* échelle énergétique — apparition en cascade */}
      <g fontFamily="'Outfit',sans-serif" fontSize="14" fontWeight="700">
        {scale.map((s, i) => (
          <g
            key={s.l}
            transform={`translate(947 ${s.y})`}
            style={{
              opacity: on ? s.op : 0,
              translate: on ? "0 0" : "14px 0",
              transition: on
                ? `opacity 0.6s ease ${0.5 + i * 0.12}s, translate 0.6s cubic-bezier(0.2,0.7,0.3,1) ${0.5 + i * 0.12}s`
                : "none",
            }}
          >
            {s.solid ? (
              <rect width={s.w} height={s.h} rx={s.r} fill={s.c} />
            ) : (
              <rect width={s.w} height={s.h} rx={s.r} fill={s.c} fillOpacity={s.fo} stroke={s.c} strokeWidth="1" />
            )}
            <text x="17" y={s.solid ? 20 : 19} fill={s.solid ? "#173D2D" : s.c}>
              {s.l}
            </text>
            {s.solid && <path d="M84 10L94 15L84 20" stroke="#173D2D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
          </g>
        ))}
      </g>
      <style>{`.cee26-pulse{animation:cee26pulse 3.2s ease-in-out infinite}@keyframes cee26pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
    </svg>
  );
}

/* FAQ en cartes numérotées avec toggle +/− (maquette « sans jargon ») */
function FaqCards() {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3.5">
      {FAQ.map(([q, a], i) => {
        const isOpen = open === i;
        return (
          <div key={q} className={`rounded-2xl border bg-card transition-all duration-300 ${isOpen ? "border-primary/25 shadow-sm" : "border-border"}`}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="flex w-full items-center gap-4 px-6 py-5 text-left md:gap-5 md:px-7"
            >
              <span className="text-sm font-semibold tabular-nums text-muted-foreground/70">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display flex-1 text-[17px] font-bold leading-snug text-primary md:text-lg">{q}</span>
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xl leading-none transition-colors duration-300 ${
                  isOpen ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"
                }`}
                aria-hidden
              >
                {isOpen ? "−" : "+"}
              </span>
            </button>
            {isOpen && (
              <div className="px-6 pb-6 md:px-7">
                <p className="ml-8 max-w-2xl text-[15px] leading-relaxed text-muted-foreground md:ml-10">{a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PrimesCee() {
  return (
    <div>
      <Seo
        title="Primes CEE — faites financer vos travaux"
        description="Certificats d'Économies d'Énergie : Energy Market compare les offres de valorisation CEE et vous obtient la meilleure prime pour vos travaux. Service 100 % gratuit. À jour 6ᵉ période 2026-2030."
        path="/primes-cee"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }}
      />

      <PageHeroPhoto
        cmsKey="cee"
        eyebrow="Primes CEE · 6ᵉ période 2026-2030"
        title="Primes CEE : faites financer vos travaux d'économies d'énergie"
        subtitle="Les fournisseurs d'énergie ont l'obligation de financer vos travaux. Energy Market vérifie votre éligibilité, cible les opérations sans reste à charge et gère le dossier jusqu'au versement de la prime."
        image="/img/primes-cee-hero.jpg"
        overlay="brand-light"
        parallax
        ctaLabel="Estimer ma prime"
        ctaTo="#estimation"
        highlightsLabel="Pourquoi passer par Energy Market"
        highlightImages={[
          "/img/pictogrammes_energy_market_svg/cee_cumul_prime.svg",
          "/img/pictogrammes_energy_market_svg/cee_renovation.svg",
          "/img/pictogrammes_energy_market_svg/sans_engagement.svg",
          "/img/pictogrammes_energy_market_svg/pro_suivi.svg",
        ]}
        highlights={[
          "Valorisation de toutes les offres éligibles",
          "Rénovation offerte jusqu'à 100%*",
          "0 € d'avance sur les travaux",
          "Dossier intégralement géré par Energy Market",
        ]}
        highlightsNote="*Selon la fiche produit."
        bottomCards={[
          { label: "Pompe à chaleur", image: "/img/travaux/pompe-a-chaleur.webp", to: "#estimation" },
          { label: "Isolation", image: "/img/travaux/isolation.webp", to: "#estimation" },
          { label: "Photovoltaïque", image: "/img/travaux/photovoltaique.webp", to: "#estimation" },
          { label: "Climatisation", image: "/img/travaux/climatisation.webp", to: "#estimation" },
        ]}
      />

      {/* ── Les CEE en bref ── */}
      <Section
        className="em-grain lg:min-h-svh lg:flex lg:flex-col lg:justify-center"
        cmsKey="cee.bref"
        eyebrow="Comprendre"
        title="Les CEE en bref"
        intro="Le dispositif des Certificats d'Économies d'Énergie impose aux vendeurs d'énergie (les « obligés ») de financer des économies d'énergie chez les Français. Concrètement : une partie de vos travaux est payée par eux, sous forme de prime."
      >
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              img: "/img/pictogrammes_energy_market_svg/cee_dispositif_etat.svg",
              title: "Un dispositif d'État",
              text: "Créé en 2005 par la loi POPE, piloté par le ministère de la Transition écologique. La 6ᵉ période (2026-2030) fixe une obligation record de 5 250 TWh cumac — les financements disponibles augmentent fortement.",
            },
            {
              img: "/img/pictogrammes_energy_market_svg/cee_tous_profils.svg",
              title: "Pour (presque) tout le monde",
              text: "Ménages, copropriétés, entreprises, collectivités, bailleurs sociaux : chaque profil a ses opérations éligibles, du remplacement d'une chaudière à la rénovation complète d'un site tertiaire.",
            },
            {
              img: "/img/pictogrammes_energy_market_svg/cee_cumul_prime.svg",
              title: "Cumulable et sans avance",
              text: "La prime CEE se cumule avec MaPrimeRénov', la TVA à 5,5 % et l'éco-prêt à taux zéro. Elle peut être déduite directement du devis ou versée après travaux — vous n'avancez rien sur cette part.",
            },
          ].map((c, i) => (
            <TraceCard key={c.title} index={i} radius={24} accent={TRACE_COLORS[i]} className="rounded-3xl border border-border bg-card text-card-foreground h-full">
              <CardContent className="p-7">
                <img src={c.img} alt="" aria-hidden loading="lazy" decoding="async" className="mb-4 h-14 w-14 transition-transform duration-300 group-hover:scale-110" />
                <h3 className="font-display text-xl font-bold text-primary leading-snug">{c.title}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{c.text}</p>
              </CardContent>
            </TraceCard>
          ))}
        </div>
      </Section>

      {/* ── Ce qui change en 2026 — bandeau sombre ── */}
      <section className="bg-primary text-primary-foreground overflow-hidden lg:min-h-svh lg:flex lg:flex-col lg:justify-center">
        <div className="em-container em-section">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Réglementation · P6</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 max-w-xl">
                Ce qui change en 2026 avec la 6ᵉ période
              </h2>
              <span aria-hidden className="mt-5 block h-1 w-16 rounded-full bg-accent" />
              <p className="text-primary-foreground/75 mt-6 max-w-xl leading-relaxed">
                La P6 a démarré le 1ᵉʳ janvier 2026. Beaucoup de contenus en ligne datent encore de la période précédente — voici ce qu'il faut réellement savoir.
              </p>
            </div>
            <div className="hidden md:flex justify-end">
              <Illu2026 />
            </div>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {CHANGES_2026.map((it, i) => (
              <div key={it.title} className="rounded-2xl border border-white/15 bg-white/[0.06] p-5 transition-colors hover:bg-white/10 md:p-6">
                {/* Mobile : tuile-icône + numéro & titre en ligne, texte dessous */}
                <div className="flex gap-4 md:hidden">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-accent/40 bg-accent/5 text-accent">
                    <it.icon className="h-8 w-8" strokeWidth={1.5} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent font-display text-sm font-black text-accent-foreground">
                        {i + 1}
                      </span>
                      <h3 className="font-display text-[17px] font-bold leading-snug text-white">{it.title}</h3>
                    </div>
                    <p className="mt-2.5 text-sm leading-relaxed text-white/70">{it.text}</p>
                  </div>
                </div>
                {/* Desktop : layout validé (numéro + picto cerclé, séparateur, texte) */}
                <div className="hidden items-stretch gap-5 md:flex">
                  <div className="flex flex-col items-center justify-center gap-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent font-display text-sm font-black text-accent-foreground">
                      {i + 1}
                    </span>
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-accent/50 text-accent">
                      <it.icon className="h-7 w-7" strokeWidth={1.5} aria-hidden />
                    </span>
                  </div>
                  <span aria-hidden className="w-px self-stretch bg-white/20" />
                  <div className="min-w-0 self-center">
                    <h3 className="font-display text-[17px] font-bold text-white">{it.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/70">{it.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Frise 2025 → 2026 → 2030 */}
          <div className="mt-14 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-4">
            {TIMELINE_P6.map((t, i) => (
              <div key={t.year} className="contents">
                <div className="flex items-center gap-4 sm:flex-col sm:gap-2.5 sm:text-center">
                  <span
                    className={`h-5 w-5 shrink-0 rounded-full border-[3px] ${
                      t.now
                        ? "border-accent bg-accent/25 shadow-[0_0_16px_rgba(196,240,66,0.75)]"
                        : "border-white/45"
                    }`}
                    aria-hidden
                  />
                  <div className="sm:text-center">
                    <p className={`font-display text-xl font-bold leading-tight ${t.now ? "text-accent" : "text-white"}`}>{t.year}</p>
                    <p className={`text-sm ${t.now ? "text-accent/90" : "text-white/60"}`}>{t.label}</p>
                  </div>
                </div>
                {i < TIMELINE_P6.length - 1 && (
                  <span aria-hidden className="hidden flex-1 sm:flex items-center gap-2 mt-[9px]">
                    {i === 0 ? (
                      <>
                        <span className="h-0.5 flex-1 [background-image:repeating-linear-gradient(90deg,rgba(255,255,255,0.32)_0_6px,transparent_6px_13px)]" />
                        <span className="h-[2.5px] w-16 rounded-full bg-accent shadow-[0_0_10px_rgba(196,240,66,0.7)]" />
                      </>
                    ) : (
                      <>
                        <span className="h-[2.5px] w-16 rounded-full bg-accent shadow-[0_0_10px_rgba(196,240,66,0.7)]" />
                        <span className="h-0.5 flex-1 [background-image:repeating-linear-gradient(90deg,rgba(255,255,255,0.32)_0_6px,transparent_6px_13px)]" />
                      </>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Particuliers : les deux Coups de pouce ── */}
      <Section
        className="lg:min-h-svh lg:flex lg:flex-col lg:justify-center"
        cmsKey="cee.part"
        eyebrow="Particuliers"
        title="Vos deux grandes primes bonifiées"
        intro="Les « Coups de pouce » sont des primes CEE majorées pour les travaux les plus efficaces. Voici les deux dispositifs phares en 2026."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Coup de pouce Chauffage */}
          <TraceCard index={0} radius={24} accent="#2E7D62" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-4">
                <img src="/img/pictogrammes_energy_market_svg/cee_chauffage.svg" alt="" aria-hidden loading="lazy" decoding="async" className="mb-4 h-14 w-14 transition-transform duration-300 group-hover:scale-110" />
                <div className="shrink-0 rounded-xl bg-accent/15 ring-1 ring-accent/40 px-4 py-2 text-right">
                  <p className="text-[11px] text-muted-foreground">Prime indicative</p>
                  <p className="font-display text-lg font-bold text-primary leading-tight">jusqu'à ≈ 7 500 €</p>
                </div>
              </div>
              <p className="em-eyebrow mb-2">Coup de pouce</p>
              <h3 className="font-display text-2xl font-bold text-primary leading-snug">Chauffage</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
                {[
                  "Remplacement d'une chaudière fioul, charbon ou gaz par une PAC air/eau ou géothermique, une chaudière biomasse ou un raccordement à un réseau de chaleur renouvelable",
                  "Ouvert à tous les ménages — propriétaires, bailleurs, locataires (avec accord) — montants majorés pour les revenus modestes",
                  "Logement de plus de 2 ans, installation par un professionnel RGE, dépose de l'ancienne chaudière",
                ].map((x) => (
                  <li key={x} className="flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    {x}
                  </li>
                ))}
                <li className="flex gap-2 text-foreground">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#b97e1b" }} />
                  <span>
                    <b>Nouveau en 2026 :</b> montant calculé selon la performance (Etas), la zone climatique et la surface — il n'existe plus de barème forfaitaire, le « jusqu'à ≈ 7 500 € » est une estimation (géothermie, grande surface). Les PAC hybrides sortent du dispositif.
                  </span>
                </li>
              </ul>
              <div className="mt-auto pt-6">
                <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                  <a href="#estimation">Estimer ma prime chauffage</a>
                </Button>
              </div>
            </CardContent>
          </TraceCard>

          {/* Coup de pouce Rénovation d'ampleur */}
          <TraceCard index={1} radius={24} accent="#7AB317" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-4">
                <img src="/img/pictogrammes_energy_market_svg/cee_renovation.svg" alt="" aria-hidden loading="lazy" decoding="async" className="mb-4 h-14 w-14 transition-transform duration-300 group-hover:scale-110" />
                <div className="shrink-0 rounded-xl bg-accent/15 ring-1 ring-accent/40 px-4 py-2 text-right">
                  <p className="text-[11px] text-muted-foreground">De 2 350 € à</p>
                  <p className="font-display text-lg font-bold text-primary leading-tight">plus de 9 600 €</p>
                </div>
              </div>
              <p className="em-eyebrow mb-2">Coup de pouce</p>
              <h3 className="font-display text-2xl font-bold text-primary leading-snug">Rénovation d'ampleur</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
                {[
                  "Gain d'au moins 2 classes au DPE, audit énergétique préalable et au minimum 2 gestes d'isolation (toiture, murs, planchers, fenêtres)",
                  "Réservé aux ménages modestes et très modestes — montants doublés pour eux. Voie alternative au parcours accompagné de MaPrimeRénov' (non cumulable avec lui)",
                  "Accompagnement complet : audit, choix des artisans, montage du dossier, versement de la prime",
                ].map((x) => (
                  <li key={x} className="flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    {x}
                  </li>
                ))}
                <li className="flex gap-2 text-foreground">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#b97e1b" }} />
                  <span>
                    <b>Bonne nouvelle 2026 :</b> le dispositif est prolongé sur toute la 6ᵉ période, avec engagement possible jusqu'à fin 2030 (80ᵉ arrêté CEE). À noter : les résidences secondaires en sont exclues depuis janvier 2026.
                  </span>
                </li>
              </ul>
              <div className="mt-auto pt-6">
                <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                  <a href="#estimation">Étudier mon projet</a>
                </Button>
              </div>
            </CardContent>
          </TraceCard>
        </div>
      </Section>

      {/* ── Parcours — stepper « étape par étape » ── */}
      <section id="parcours" className="scroll-mt-24 lg:min-h-svh lg:flex lg:flex-col lg:justify-center">
        <div className="em-container em-section">
          {/* Entête + mini-stats */}
          <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-center">
            <div>
              <p className="em-eyebrow mb-3">Parcours CEE</p>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-primary md:text-5xl">
                Votre prime CEE, étape par étape.
              </h2>
              <p className="mt-4 text-muted-foreground">Un parcours clair, accompagné par nos experts, jusqu'au versement.</p>
            </div>
            <div className="flex items-stretch lg:justify-end">
              {[
                { icon: Flag, title: "4 étapes", text: "Un parcours simple et sécurisé." },
                { icon: UserRound, title: "Un conseiller dédié", text: "Un expert vous accompagne à chaque étape." },
                { icon: Award, title: "Professionnels RGE", text: "Des partenaires qualifiés et certifiés." },
              ].map((m, i) => (
                <div key={m.title} className={`flex max-w-[190px] flex-col items-center px-5 text-center sm:px-7 ${i ? "border-l border-border" : ""}`}>
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-primary">
                    <m.icon className="h-7 w-7" strokeWidth={1.4} aria-hidden />
                  </span>
                  <p className="font-display mt-3 font-bold leading-tight text-primary">{m.title}</p>
                  <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{m.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Carte stepper */}
          <div className="mt-12 overflow-hidden rounded-3xl border border-border bg-[#F5F6F1] shadow-sm">
            <div className="relative grid md:grid-cols-4">
              {/* ligne de connexion entre les numéros */}
              <div aria-hidden className="pointer-events-none absolute left-[12.5%] right-[12.5%] top-[67px] hidden h-0.5 bg-accent md:block" />
              {[
                { num: "01", icon: FileText, title: "Décrivez votre projet", text: "Profil, travaux et localisation : 2 minutes suffisent." },
                { num: "02", icon: Scale, title: "Comparez les offres", text: "Montant, conditions et délai de versement, en toute clarté." },
                { num: "03", icon: ShieldCheck, title: "Validez votre prime", text: "Acceptez l'offre CEE avant de signer votre devis.", hot: true },
                { num: "04", icon: Home, title: "Réalisez vos travaux", text: "Un professionnel RGE intervient, puis votre prime est déduite ou versée." },
              ].map((st, i) => (
                <div
                  key={st.num}
                  className={`group relative px-6 pb-10 pt-10 text-center transition-colors duration-300 hover:bg-accent/15 ${
                    i ? "border-t border-border md:border-l md:border-t-0" : ""
                  }`}
                >
                  <div className="relative flex justify-center">
                    <span
                      className={`z-10 flex h-14 w-14 items-center justify-center rounded-full font-display text-lg font-extrabold ring-4 ring-[#F5F6F1] transition-transform duration-300 group-hover:scale-110 ${
                        st.hot ? "bg-accent text-accent-foreground shadow-[0_0_18px_rgba(196,240,66,0.6)]" : "bg-primary text-primary-foreground"
                      }`}
                    >
                      {st.num}
                    </span>
                  </div>
                  <div className="mt-3 h-7">
                    {st.hot && (
                      <span className="inline-block rounded-md bg-primary px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white">
                        À faire avant le devis
                      </span>
                    )}
                  </div>
                  <st.icon
                    className="mx-auto mt-4 h-12 w-12 text-primary transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110"
                    strokeWidth={1.25}
                    aria-hidden
                  />
                  <h3 className="font-display mt-5 text-lg font-bold text-primary">{st.title}</h3>
                  <p className="mx-auto mt-2 max-w-[250px] text-sm leading-relaxed text-muted-foreground">{st.text}</p>
                </div>
              ))}
            </div>

            {/* Bandeau « bon réflexe » */}
            <div className="flex flex-col gap-4 bg-primary px-6 py-5 sm:flex-row sm:items-center sm:justify-between md:px-10">
              <div className="flex items-center gap-3.5 text-white">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-accent font-display text-lg font-bold text-accent">!</span>
                <p className="text-sm md:text-[15px]">
                  <b>Le bon réflexe :</b> acceptez votre offre de prime CEE avant de signer votre devis.
                </p>
              </div>
              <Button asChild className="h-12 shrink-0 rounded-full bg-accent px-7 font-bold text-accent-foreground hover:bg-accent/90">
                <a href="#estimation" className="flex items-center gap-3">
                  Vérifier mon éligibilité <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Estimation — wizard sur fond photo ── */}
      <section id="estimation" className="relative overflow-hidden scroll-mt-24 lg:min-h-svh lg:flex lg:flex-col lg:justify-center">
        <div className="absolute inset-0 bg-primary">
          <picture>
            <source srcSet="/img/primes-cee-hero.webp" type="image/webp" />
            <img src="/img/primes-cee-hero.jpg" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/55 to-primary/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/45 via-transparent to-transparent" />
        </div>
        <div className="relative em-container em-section">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Estimation gratuite</p>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight text-white mt-3">
                Estimez votre prime CEE
              </h2>
              <p className="mt-5 max-w-lg leading-relaxed text-white/85">
                Décrivez votre projet en 2 minutes : un conseiller vérifie votre éligibilité, identifie les bonifications applicables et monte le dossier avec vous.
              </p>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/60">
                Déjà client Energy Market pour votre électricité ou votre gaz ? Votre conseiller connaît déjà votre dossier.
              </p>
              <div className="mt-9 flex flex-wrap items-stretch gap-y-4">
                {[
                  { img: "/img/pictogrammes_energy_market_svg/estimation_2_minutes.svg", label: "2 minutes" },
                  { img: "/img/pictogrammes_energy_market_svg/sans_engagement.svg", label: "Sans engagement" },
                  { img: "/img/pictogrammes_energy_market_svg/reponse_24h.svg", label: "Réponse sous 24 h" },
                ].map((b, i) => (
                  <div key={b.label} className={`flex flex-col items-center gap-2.5 px-5 sm:px-7 first:pl-0 ${i ? "border-l border-white/20" : ""}`}>
                    <img src={b.img} alt="" aria-hidden className="h-12 w-12" loading="lazy" decoding="async" />
                    <span className="text-sm font-medium text-white/90 text-center">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <EstimWizard />
          </div>
        </div>
      </section>

      {/* ── FAQ — « On vous répond sans jargon. » ── */}
      <section className="relative overflow-hidden lg:min-h-svh lg:flex lg:flex-col lg:justify-center">
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: "linear-gradient(135deg, rgba(196,240,66,0.16), rgba(249,250,249,0) 45%)" }} />
        <div aria-hidden className="absolute -bottom-40 -left-40 -z-10 h-[420px] w-[420px] rounded-full border-[26px] border-primary/5" />
        <div aria-hidden className="absolute -bottom-64 -left-64 -z-10 h-[660px] w-[660px] rounded-full border-[26px] border-primary/[0.03]" />

        <div className="em-container em-section">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="em-eyebrow mb-3">Questions fréquentes</p>
              <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-primary md:text-5xl">
                Primes CEE : vos questions
              </h2>
              <p className="mt-5 max-w-sm leading-relaxed text-muted-foreground">
                Montant, délais, cumul des aides… retrouvez l'essentiel avant de lancer votre projet.
              </p>
              <a
                href="#estimation"
                className="group mt-9 flex max-w-md items-center gap-4 rounded-2xl bg-primary p-4 pr-5 text-white transition-transform duration-300 hover:-translate-y-0.5"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent font-display text-xl font-bold text-accent-foreground">?</span>
                <span className="flex-1">
                  <span className="font-display block font-bold">Une question plus précise ?</span>
                  <span className="block text-sm text-white/70">Un conseiller vous répond sous 24 h.</span>
                </span>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-primary transition-transform duration-300 group-hover:translate-x-0.5">
                  <ArrowRight className="h-5 w-5" aria-hidden />
                </span>
              </a>

              <div className="mt-5 max-w-md rounded-2xl border border-border bg-card p-6 shadow-sm">
                <p className="em-eyebrow mb-4">À retenir</p>
                <div className="grid grid-cols-3">
                  {[
                    { t: "Étude gratuite", s: "Sans engagement" },
                    { t: "Offres comparées", s: "Plusieurs obligés consultés" },
                    { t: "Dossier sécurisé", s: "Suivi jusqu'au versement" },
                  ].map((it, i) => (
                    <div key={it.t} className={`px-3.5 first:pl-0 last:pr-0 ${i ? "border-l border-border" : ""}`}>
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground" aria-hidden>
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        </span>
                        <div>
                          <p className="font-display text-[15px] font-bold leading-tight text-primary">{it.t}</p>
                          <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{it.s}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <FaqCards />
              <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
                Montants indicatifs à jour de juillet 2026, susceptibles d'évoluer selon la réglementation (6ᵉ période CEE 2026-2030), la performance des équipements, la zone climatique et les revenus du ménage. Le montant définitif figure dans l'offre de prime remise avant signature des devis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA final ── */}
      <section className="em-container pt-2 pb-12 md:pb-16">
        <div className="bg-primary rounded-3xl text-primary-foreground px-8 py-12 md:px-16 md:py-14 flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-10">
          <div className="md:flex-1">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Un projet de travaux ? Chiffrons votre prime.</h2>
            <p className="text-primary-foreground/80 mt-4 max-w-xl">
              Estimation gratuite et sans engagement — nous comparons les offres de valorisation pour vous.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
              <a href="#estimation">Estimer ma prime</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <Link to="/contact">Être rappelé</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
